/**
 * AdditionalViewsTab — Ustawienia → „Widoki dodatkowe" (F22, ADR-0087).
 *
 * Katalog widoków dodatkowych (sekcje SQL read-only na formatce kartoteki) — mirror
 * `ProcessProceduresTab.tsx` (lista + modal create/edit + „Testuj" w modalu) skrzyżowany z
 * `AdditionalTablesTab.tsx` (filtr po kartotece nadrzędnej, ten sam koncept „rodzica"). Katalog
 * jest tenant-globalny (bez `company_id`), bramkowany JEDNYM uprawnieniem tenant-wide
 * `crm_core.additional_views.manage` (ADR-0087 Decyzja 2, mirror `processes.procedures.manage`)
 * — podgląd/CRUD/test wszystko pod tym samym kodem, bez osobnych uprawnień per akcja.
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.alert/confirm/prompt — ConfirmDialog + useToast.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdditionalViewDefinition } from '@saas/shared';
import { isAxiosError } from 'axios';
import { Database, Pencil, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  useAdditionalViewsList,
  useCreateAdditionalView,
  useDeleteAdditionalView,
  useUpdateAdditionalView,
} from '@/lib/additional-views/api';

import {
  AdditionalViewFormModal,
  type AdditionalViewFormValues,
  emptyAdditionalViewFormValues,
} from './AdditionalViewFormModal';

/** Etykieta kartoteki nadrzędna — mirror `parentLabel` (`AdditionalTablesTab.tsx`), reużywa
 *  TE SAME klucze i18n (koncepcja identyczna dla obu katalogów). */
function parentLabel(t: ReturnType<typeof useTranslation>['t'], parentEntity: string): string {
  const key = `additionalTables.admin.parentOptions.${toCamel(parentEntity)}`;
  const translated = t(key);
  return translated === key ? parentEntity : translated;
}

function toCamel(snake: string): string {
  return snake.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

export function AdditionalViewsTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const toast = useToast();

  const isSuperUser = me?.user.isSuperUser === true;
  const canManage =
    isSuperUser || (me?.permissions.includes('crm_core.additional_views.manage') ?? false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdditionalViewDefinition | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdditionalViewDefinition | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const listQuery = useAdditionalViewsList();
  const createMutation = useCreateAdditionalView();
  const updateMutation = useUpdateAdditionalView();
  const deleteMutation = useDeleteAdditionalView();

  if (!canManage) {
    return (
      <Card padding="md" data-testid="additional-views-tab-no-permission">
        <p className="text-sm text-text-tertiary">{t('additionalViews.admin.noPermission')}</p>
      </Card>
    );
  }

  const views = listQuery.data ?? [];

  const formInitialValues: AdditionalViewFormValues = editing
    ? {
        displayName: editing.displayName,
        slug: editing.slug,
        parentTableName: editing.parentTableName,
        parentEntityType: editing.parentEntityType,
        parentEntity: editing.parentEntity,
        description: editing.description ?? '',
        sourceSql: editing.sourceSql,
        declaredParams: editing.declaredParams,
        paramBindings: editing.paramBindings,
      }
    : emptyAdditionalViewFormValues();

  function handleCreate(values: AdditionalViewFormValues) {
    setServerErrors({});
    createMutation.mutate(
      {
        displayName: values.displayName,
        slug: values.slug,
        parentTableName: values.parentTableName,
        parentEntityType: values.parentEntityType,
        parentEntity: values.parentEntity,
        description: values.description.trim() || null,
        sourceSql: values.sourceSql,
        declaredParams: values.declaredParams,
        paramBindings: values.paramBindings,
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('additionalViews.admin.toast.created') });
          setCreateOpen(false);
        },
        onError: (err) => {
          const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
          if (code === 'SATELLITE_SLUG_DUPLICATE' || code === 'ADDITIONAL_VIEW_SLUG_DUPLICATE') {
            toast.show({
              variant: 'warning',
              title: t('additionalViews.admin.toast.slugDuplicate'),
            });
            setServerErrors({ slug: t('additionalViews.admin.toast.slugDuplicate') });
            return;
          }
          toast.show({ variant: 'error', title: t('additionalViews.admin.toast.createError') });
        },
      },
    );
  }

  function handleUpdate(id: string, values: AdditionalViewFormValues) {
    setServerErrors({});
    updateMutation.mutate(
      {
        id,
        values: {
          displayName: values.displayName,
          description: values.description.trim() || null,
          sourceSql: values.sourceSql,
          declaredParams: values.declaredParams,
          paramBindings: values.paramBindings,
        },
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('additionalViews.admin.toast.updated') });
          setEditing(null);
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('additionalViews.admin.toast.updateError') });
        },
      },
    );
  }

  function handleDelete(view: AdditionalViewDefinition) {
    deleteMutation.mutate(view.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('additionalViews.admin.toast.deleted') });
        setPendingDelete(null);
      },
      onError: () => {
        toast.show({ variant: 'error', title: t('additionalViews.admin.toast.deleteError') });
        setPendingDelete(null);
      },
    });
  }

  return (
    <div data-testid="additional-views-tab">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('additionalViews.admin.title')}
          </h2>
          <p className="text-xs text-text-tertiary">{t('additionalViews.admin.subtitle')}</p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setServerErrors({});
            setCreateOpen(true);
          }}
          data-testid="additional-views-new-btn"
        >
          {t('additionalViews.admin.newView')}
        </Button>
      </header>

      {listQuery.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : listQuery.isError ? (
        <Card padding="md">
          <p className="text-sm text-error-500">{t('additionalViews.admin.loadError')}</p>
        </Card>
      ) : views.length === 0 ? (
        <Card padding="md" data-testid="additional-views-empty">
          <p className="text-sm text-text-tertiary">{t('additionalViews.admin.empty')}</p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('additionalViews.admin.columns.name')}</th>
                  <th className="px-4 py-2 text-left">
                    {t('additionalViews.admin.columns.parent')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('additionalViews.admin.columns.params')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('additionalViews.admin.columns.active')}
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {views.map((view) => (
                  <tr key={view.id} data-testid={`additional-view-row-${view.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4 text-text-muted" aria-hidden="true" />
                        <div>
                          <div className="font-medium text-text-primary">{view.displayName}</div>
                          <div className="font-mono text-xs text-text-tertiary">{view.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {parentLabel(t, view.parentEntity)}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">{view.declaredParams.length}</td>
                    <td className="px-4 py-3">
                      {view.isActive ? (
                        <Badge color="accent">{t('additionalViews.admin.activeLabel')}</Badge>
                      ) : (
                        <Badge color="gray">{t('additionalViews.admin.inactiveLabel')}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setServerErrors({});
                            setEditing(view);
                          }}
                          aria-label={t('common.edit')}
                          data-testid={`additional-view-edit-${view.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </IconButton>
                        <IconButton
                          variant="danger"
                          size="sm"
                          onClick={() => setPendingDelete(view)}
                          aria-label={t('common.delete')}
                          data-testid={`additional-view-delete-${view.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AdditionalViewFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'create'}
        initialValues={formInitialValues}
        serverErrors={serverErrors}
        saving={createMutation.isPending || updateMutation.isPending}
        onSubmit={(values) => {
          if (editing) {
            handleUpdate(editing.id, values);
          } else {
            handleCreate(values);
          }
        }}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
          setServerErrors({});
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('additionalViews.admin.confirmDelete.title')}
        message={
          pendingDelete
            ? t('additionalViews.admin.confirmDelete.message', { name: pendingDelete.displayName })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
